import { Bookmark, ChevronRight, Clock, Undo2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import StatusChip from './StatusChip';
import { tx } from '../i18n';
import { formatDate } from '../lib/format';
import { verdictShort } from '../lib/report';
import { forgetReport, rememberedReports, restoreReport } from '../lib/saved';
import type { RememberedReport } from '../lib/saved';
import { statusOfVerdict } from '../lib/status';
import { num } from '../lib/values';

/** How long a removed report can be brought back before the offer goes away. */
const UNDO_MS = 8000;

/**
 * The way back to a report you have already paid attention to.
 *
 * <p>Somebody viewing four flats in a weekend runs four reports and then wants the second one
 * again. Without an account the only place that list can live is this browser, so the home page
 * offers it: the ones they kept first, then the ones they merely opened.
 *
 * <p>The whole row opens the report - a link stretched over the row rather than wrapped around it,
 * so the remove button inside it is still a button and not part of the link. Removing is offered
 * on every row, because a list of addresses somebody has looked at is theirs to clear, and it is
 * undoable, because it is one small press next to a list of near-identical lines.
 */
export default function RecentReports() {
  const [entries, setEntries] = useState(() => rememberedReports());
  const [undo, setUndo] = useState<RememberedReport | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
    }
  }, []);

  if (entries.length === 0 && undo === null) {
    return null;
  }

  const saved = entries.filter((entry) => entry.saved);
  const recent = entries.filter((entry) => !entry.saved).slice(0, 4);
  const shown = [...saved, ...recent];

  const forget = (entry: RememberedReport) => {
    forgetReport(entry.analysisId);
    setEntries(rememberedReports());
    setUndo(entry);
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
    }
    timer.current = window.setTimeout(() => setUndo(null), UNDO_MS);
  };

  const bringBack = () => {
    if (!undo) {
      return;
    }
    restoreReport(undo);
    setEntries(rememberedReports());
    setUndo(null);
  };

  return (
    <section className="stack stack--tight" aria-labelledby="recent-heading">
      <h2 id="recent-heading" className="card__title">
        {saved.length > 0
          ? tx('Your properties', 'Konutlarınız', 'Twoje mieszkania', 'Os seus imóveis', 'Tus viviendas')
          : tx('Recent reports', 'Son raporlar', 'Ostatnie raporty', 'Relatórios recentes', 'Informes recientes')}
      </h2>
      <p className="small muted">
        {tx(
          'Kept on this device only, so they are here on this browser and nowhere else.',
          'Yalnızca bu cihazda saklanır; bu tarayıcıda vardır, başka yerde yoktur.',
          'Przechowywane tylko na tym urządzeniu — są w tej przeglądarce i nigdzie indziej.',
          'Guardados apenas neste dispositivo, pelo que existem neste navegador e em mais lado nenhum.',
          'Se guardan solo en este dispositivo, así que están en este navegador y en ningún otro sitio.',
        )}
      </p>

      {undo ? (
        <p className="undo-bar small" role="status">
          <span>
            {tx(
              `Removed ${undo.addressLine} from this list.`,
              `${undo.addressLine} bu listeden çıkarıldı.`,
              `Usunięto ${undo.addressLine} z tej listy.`,
              `${undo.addressLine} foi removida desta lista.`,
              `Se ha quitado ${undo.addressLine} de esta lista.`,
            )}
          </span>
          <button type="button" className="link-button" onClick={bringBack}>
            <Undo2 size={14} aria-hidden="true" />
            {tx('Undo', 'Geri al', 'Cofnij', 'Anular', 'Deshacer')}
          </button>
        </p>
      ) : null}

      {shown.length > 0 ? (
        <ul className="recent-list">
          {shown.map((entry) => {
            const score = num(entry.score);
            return (
              <li key={entry.analysisId} className="recent-item">
                {entry.saved ? (
                  <Bookmark size={16} aria-hidden="true" className="recent-item__icon" />
                ) : (
                  <Clock size={16} aria-hidden="true" className="recent-item__icon" />
                )}
                <span className="recent-item__text">
                  {/* Stretched over the row, so the whole line is the target and the button is not. */}
                  <Link to={`/report/${entry.analysisId}`} className="recent-item__link">
                    <span className="recent-item__address">{entry.addressLine}</span>
                  </Link>
                  <span className="small muted">
                    {entry.postcode}
                    {' · '}
                    {tx(
                      `Opened ${formatDate(entry.openedAt)}`,
                      `Açıldı: ${formatDate(entry.openedAt)}`,
                      `Otwarte ${formatDate(entry.openedAt)}`,
                      `Aberto em ${formatDate(entry.openedAt)}`,
                      `Abierto el ${formatDate(entry.openedAt)}`,
                    )}
                  </span>
                </span>

                {score !== null && entry.label !== null ? (
                  <StatusChip
                    status={statusOfVerdict(entry.label)}
                    label={`${score}/100 · ${verdictShort(entry.label)}`}
                  />
                ) : null}

                <ChevronRight size={16} aria-hidden="true" className="recent-item__chevron" />

                <button
                  type="button"
                  className="icon-button recent-item__remove"
                  onClick={() => forget(entry)}
                  title={tx('Remove report', 'Raporu çıkar', 'Usuń raport', 'Remover relatório', 'Quitar informe')}
                >
                  <X size={16} aria-hidden="true" />
                  <span className="sr-only">
                    {tx(
                      `Remove report for ${entry.addressLine} from this list`,
                      `${entry.addressLine} raporunu bu listeden çıkar`,
                      `Usuń raport dla ${entry.addressLine} z tej listy`,
                      `Remover o relatório de ${entry.addressLine} desta lista`,
                      `Quitar el informe de ${entry.addressLine} de esta lista`,
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
